// Estado central de la partida (vidas, tiempo, puntuación, racha, progreso).
// Es un EventEmitter para que el HUD y los efectos reaccionen sin acoplarse a la escena.
// La lógica de puntuación vive en ScoreSystem (separada y verificable).

import Phaser from 'phaser';
import { ScoreSystem } from './ScoreSystem';
import { LEVEL } from '../config';

export class GameState extends Phaser.Events.EventEmitter {
  constructor({ time = LEVEL.START_TIME, challenges = LEVEL.CHALLENGES } = {}) {
    super();
    this.scoreSys = new ScoreSystem();
    this.maxLives = LEVEL.MAX_LIVES;
    this.lives = LEVEL.MAX_LIVES;
    this.startTime = time;
    this.timeLeft = time;
    // Tiempo real independiente del reloj de juego (que recibe premios y penalizaciones).
    this.elapsedTime = 0;
    this.speedBonus = 0;
    this.streak = 0;
    this.bestStreak = 0;
    this.solved = 0;
    this.wrongCount = 0;
    this.totalChallenges = challenges;
    this.progress = 0;
    this.problem = null;
    this.over = false;
    this.won = false;
    // El entrenamiento inicial detiene el reloj sin detener al personaje,
    // la física ni la cámara del mundo.
    this.clockActive = true;
  }

  get score() {
    return this.scoreSys.score;
  }

  setProblem(problem) {
    this.problem = problem;
    this.emit('problem', problem);
  }

  setProgress(p) {
    this.progress = Phaser.Math.Clamp(p, 0, 1);
  }

  addTime(sec) {
    this.timeLeft = Phaser.Math.Clamp(this.timeLeft + sec, 0, this.startTime + 40);
  }

  setClockActive(active) {
    this.clockActive = !!active;
  }

  correct() {
    const bonus = this.streak * 10;
    const pts = LEVEL.BASE_POINTS + bonus;
    this.scoreSys.record('correct', pts, { streak: this.streak });
    this.streak += 1;
    this.bestStreak = Math.max(this.bestStreak, this.streak);
    this.solved += 1;
    this.addTime(LEVEL.TIME_CORRECT);
    this.emit('correct', { pts, streak: this.streak });
    return pts;
  }

  wrong() {
    this.streak = 0;
    this.wrongCount += 1;
    this.lives -= 1;
    this.timeLeft = Math.max(0, this.timeLeft - LEVEL.TIME_WRONG);
    this.emit('wrong', { lives: this.lives });
    if (this.lives <= 0) this.gameOver(false);
  }

  addBonus() {
    const pts = LEVEL.BONUS_CRYSTAL;
    this.scoreSys.record('bonus', pts, {});
    this.emit('bonus', { pts });
    return pts;
  }

  tick(dtSec) {
    if (this.over || !this.clockActive) return;
    this.elapsedTime += dtSec;
    this.timeLeft -= dtSec;
    if (this.timeLeft <= 0) {
      this.timeLeft = 0;
      this.gameOver(false);
    }
  }

  win() {
    // La rapidez solo premia una misión perfecta: todos los retos y cero errores.
    const perfect = this.solved === this.totalChallenges && this.wrongCount === 0;
    if (perfect) {
      const remainingRatio = Phaser.Math.Clamp(1 - this.elapsedTime / this.startTime, 0, 1);
      this.speedBonus = Math.round(LEVEL.PERFECT_SPEED_BONUS_MAX * remainingRatio);
      if (this.speedBonus > 0) {
        this.scoreSys.record('perfect_speed_bonus', this.speedBonus, {
          elapsedTime: Math.round(this.elapsedTime * 10) / 10,
          startTime: this.startTime,
          accuracy: 100,
        });
      }
    }
    this.gameOver(true);
  }

  gameOver(won) {
    if (this.over) return;
    this.over = true;
    this.won = won;
    this.emit('gameover', { won });
  }

  toRun() {
    const attempts = this.solved + this.wrongCount;
    return {
      score: this.score,
      bestStreak: this.bestStreak,
      solved: this.solved,
      correct: this.solved,
      wrong: this.wrongCount,
      accuracy: attempts > 0 ? Math.round((this.solved / attempts) * 100) : 0,
      total: this.totalChallenges,
      timeLeft: Math.round(this.timeLeft),
      elapsedTime: Math.round(this.elapsedTime * 10) / 10,
      speedBonus: this.speedBonus,
      scoreBeforeSpeedBonus: this.score - this.speedBonus,
      won: this.won,
      ...this.scoreSys.toRunPayload(),
    };
  }
}
